import { useCallback, useEffect, useRef, useState } from 'react';
import type { DonViTinh, GiaBanVatTuData } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

type Params = {
  vatTu: GiaBanVatTuData | null;
  setVatTu: (vatTu: GiaBanVatTuData | null) => void;
  donViTinh: DonViTinh | null;
  setDonViTinh: (donViTinh: DonViTinh | null) => void;
};

type VatTuPriceDetail = GiaBanVatTuData & {
  dvt_data?: DonViTinh | null;
  quy_doi_dvt_data?: Array<{ dvt_data?: DonViTinh | null }>;
};

export const useVatTuDvtOptions = ({ vatTu, setVatTu, donViTinh, setDonViTinh }: Params) => {
  const { entity } = useAuth();
  const [dvtOptions, setDvtOptions] = useState<DonViTinh[]>([]);
  const fetchingRef = useRef(false);

  const buildDvtOptions = useCallback(
    (vatTuDetail: VatTuPriceDetail) => {
      const options: DonViTinh[] = [
        ...(vatTuDetail?.dvt_data ? [vatTuDetail.dvt_data] : []),
        ...(vatTuDetail.quy_doi_dvt_data || [])
          .map(item => item.dvt_data)
          .filter((item): item is DonViTinh => Boolean(item))
      ];

      if (donViTinh && !options.find(opt => opt.uuid === donViTinh.uuid)) {
        options.push(donViTinh);
      }

      const uniqueOptions = options.filter(
        (option, index, self) => self.findIndex(item => item.uuid === option.uuid) === index
      );

      setDvtOptions(uniqueOptions);

      const matched = uniqueOptions.find(option => option.uuid === donViTinh?.uuid);

      if (matched) {
        setDonViTinh(matched);
      } else if (!donViTinh && uniqueOptions.length > 0) {
        setDonViTinh(uniqueOptions[0]);
      }
    },
    [donViTinh, setDonViTinh]
  );

  const fetchVatTuDetail = useCallback(
    async (vatTu: GiaBanVatTuData): Promise<VatTuPriceDetail> => {
      const { data } = await api.get<VatTuPriceDetail>(
        `/entities/${entity?.slug}/erp/${QUERY_KEYS.VAT_TU}/${vatTu.uuid}/`
      );
      return data;
    },
    [entity?.slug]
  );

  const handleVatTuSelection = useCallback(
    async (selectedVatTu: GiaBanVatTuData | null) => {
      if (!selectedVatTu || fetchingRef.current) {
        if (!selectedVatTu) {
          setVatTu(null);
          setDonViTinh(null);
          setDvtOptions([]);
        }
        return;
      }

      fetchingRef.current = true;

      try {
        const vatTuDetail = await fetchVatTuDetail(selectedVatTu);
        setVatTu(vatTuDetail);
        buildDvtOptions(vatTuDetail);
      } finally {
        fetchingRef.current = false;
      }
    },
    [buildDvtOptions, fetchVatTuDetail, setDonViTinh, setVatTu]
  );

  useEffect(() => {
    if (vatTu) {
      buildDvtOptions(vatTu);
    } else {
      setDvtOptions([]);
    }
  }, [vatTu, buildDvtOptions]);

  return {
    dvtOptions,
    handleVatTuSelection
  };
};
